import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Tooltip from 'flarum/common/components/Tooltip';
import Dropdown from 'flarum/common/components/Dropdown';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import { trans, displayName, tagPillStyle, discussionRoute, tagRoute, formatTimeLabel, truncate, highlight, navigate, userRoute, numberOr } from '../../utils';
/**
 * PostCard — shared card for rendering a user's comment inside a discussion.
 *
 * Used by:
 *  - UserProfilePage  (posts tab, likes tab, mentions tab)
 *  - AvocadoSearchPage (discussions/posts search results)
 *
 * Renders the same AvocadoHome-threadCard shell as ThreadCard but is
 * anchored to a **post** (not a discussion), linking to the post's position.
 */
export default class PostCard extends Component {
  view() {
    const {
      post,
      context,
      searchQuery = '',
      showBadges = true
    } = this.attrs;
    if (!post) return null;
    const discussion = post.discussion?.();
    if (!discussion) return null;
    const id = post.id?.();
    const user = post.user?.();
    const title = discussion.title?.() || 'Untitled';
    const postNum = post.number?.();
    const href = (() => {
      try {
        return app.route.discussion(discussion, postNum);
      } catch {
        return discussionRoute(discussion);
      }
    })();
    const tags = (discussion.tags?.() || []).filter(Boolean);
    const timeLabel = formatTimeLabel(post.createdAt?.());
    const userHref = userRoute(user);
    const plain = post.contentPlain?.() || '';
    const excerpt = plain ? searchQuery ? highlight(plain, searchQuery, 200) : truncate(plain, 200) : null;
    const titleNode = searchQuery ? highlight(title, searchQuery) : title;
    const replies = numberOr(discussion.replyCount?.(), 0);
    const isSticky = discussion.isSticky?.() || false;
    const isLocked = discussion.isLocked?.() || false;
    const controls = DiscussionControls.controls(discussion, context).toArray();
    return m("article", {
      key: id,
      className: "AvocadoHome-threadCard"
    }, m("div", {
      className: "AvocadoHome-threadHead"
    }, m("div", {
      className: "AvocadoHome-avatarWrap"
    }, user && m(Avatar, {
      user: user,
      title: displayName(user)
    })), m("div", {
      className: "AvocadoHome-threadMain"
    }, m("div", {
      className: "AvocadoHome-threadMeta"
    }, m("a", {
      className: "AvocadoHome-threadAuthor",
      href: userHref,
      onclick: e => {
        e.stopPropagation();
        navigate(e, userHref);
      }
    }, displayName(user)), timeLabel && m("span", {
      className: "AvocadoHome-threadTime"
    }, timeLabel), showBadges && isSticky && m(Tooltip, {
      text: trans('ramon-avocado.forum.home.badge_sticky', 'Pinned'),
      position: "top"
    }, m("span", {
      className: "AvocadoHome-badge AvocadoHome-badge--sticky"
    }, m("i", {
      className: "fas fa-thumbtack",
      "aria-hidden": "true"
    }))), showBadges && isLocked && m(Tooltip, {
      text: app.translator.trans('flarum-lock.forum.badge.locked_tooltip'),
      position: "top"
    }, m("span", {
      className: "AvocadoHome-badge AvocadoHome-badge--locked"
    }, m("i", {
      className: "fas fa-lock",
      "aria-hidden": "true"
    }))), tags.slice(0, 2).map(tag => m("a", {
      key: tag.id?.(),
      className: "AvocadoHome-tagPill",
      href: tagRoute(tag),
      onclick: e => {
        e.stopPropagation();
        navigate(e, tagRoute(tag));
      },
      style: tagPillStyle(tag.color?.())
    }, tag.icon?.() && m("i", {
      className: tag.icon(),
      "aria-hidden": "true"
    }), tag.name?.()))), m("a", {
      className: "AvocadoHome-threadTitle",
      href: href,
      onclick: e => navigate(e, href)
    }, titleNode), excerpt && m("p", {
      className: "AvocadoHome-threadExcerpt AvocadoUserPage-postExcerpt"
    }, excerpt)), m("div", {
      className: "AvocadoHome-threadActions"
    }, controls.length > 0 && m(Dropdown, {
      className: "AvocadoHome-threadControls",
      icon: "fas fa-ellipsis-v",
      buttonClassName: "Button Button--icon Button--flat AvocadoHome-threadControls-toggle",
      accessibleToggleLabel: app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')
    }, controls), m("a", {
      className: "AvocadoHome-replyBtn",
      href: href,
      onclick: e => {
        e.stopPropagation();
        navigate(e, href);
      }
    }, m("i", {
      className: "fas fa-arrow-right",
      "aria-hidden": "true"
    }), trans('ramon-avocado.forum.home.view', 'View')))), m("div", {
      className: "AvocadoHome-threadStats"
    }, m("span", {
      className: "AvocadoHome-statBtn AvocadoHome-statBtn--replies",
      onclick: e => {
        e.stopPropagation();
        m.route.set(href);
      }
    }, m("i", {
      className: "far fa-comment",
      "aria-hidden": "true"
    }), m("span", null, replies === 1 ? trans('ramon-avocado.forum.home.reply_singular', '1 reply') : trans('ramon-avocado.forum.home.reply_plural', '{count} replies', {
      count: replies
    })))));
  }
}
flarum.reg.add('ramon-avocado', 'forum/components/shared/PostCard', PostCard);